import {
  type MarketPipelineStage,
  type ProjectMarketResolutionAtResult,
  selectConvictionLeader,
} from '@morfi/backend-sdk-v2/client';
import type { OutcomeHueName } from '@/hooks/chart/use-outcome-color';

export interface ResolutionCardOutcome {
  readonly marketPubkey: string;
  readonly outcomeIndex: number;
  readonly label: string;
  readonly hue: OutcomeHueName;
  readonly imageUrl: string | null;
  readonly imageNsfw: boolean;
}

export type ResolutionCardState =
  | {
      readonly kind: 'projected';
      readonly outcome: ResolutionCardOutcome;
      /** Distance from the outcome's live share to the resolution threshold, fractional. */
      readonly gap: number;
      /** Wall-clock milliseconds at which the outcome's conviction reaches the threshold. */
      readonly crossAtMs: number;
    }
  | {
      /**
       * `resolving`: the leader reached the line and the crank has not landed.
       * `graduating`: the resolution landed and the page holds until it is settled.
       */
      readonly kind: 'resolving' | 'graduating';
      readonly outcome: ResolutionCardOutcome;
      /** Resolution threshold, fractional. */
      readonly threshold: number;
      /** When the outcome reached the line; the countdown holds at 0:00 from here. */
      readonly crossAtMs: number;
    };

export interface ResolutionCardInput {
  readonly marketPubkey: string;
  /** Indexed market status. The card exists only while trading is open. */
  readonly marketStatus: string;
  readonly pipelineStage: MarketPipelineStage;
  readonly outcomes:
    | ReadonlyArray<{ readonly outcomeIndex: number; readonly label: string }>
    | undefined;
  readonly outcomeHueByIndex: ReadonlyMap<number, OutcomeHueName>;
  readonly imageUrlByIndex: ReadonlyMap<number, string>;
  readonly imageNsfwByIndex: ReadonlyMap<number, boolean>;
  /** Per-outcome live conviction, fractional, keyed by outcome index. */
  readonly liveConviction: ReadonlyMap<number, number>;
  /** Resolution threshold, fractional; `null` until the market config has loaded. */
  readonly threshold: number | null;
  readonly projectedWinner: Pick<
    ProjectMarketResolutionAtResult,
    'outcomeIndex' | 'crossAtMs'
  > | null;
  /** Live distance from the projected winner to the line; from `marketResolutionView`. */
  readonly projectedGap: number | null;
  /** Crossing time to hold the countdown at once the line is reached. */
  readonly resolvingCrossAtMs: number;
  /** The resolution has landed and the page holds until the settled page is ready. */
  readonly resolutionHolding: boolean;
}

/**
 * The page is in the resolving step: presented as trading, the leader has
 * reached the line, and no landed resolution is being held yet. The card reads
 * Resolving and the resolution glow plays; graduating is a separate step.
 */
export function isResolvingStep(input: {
  readonly marketStatus: string;
  readonly pipelineStage: MarketPipelineStage;
  readonly resolutionHolding: boolean;
}): boolean {
  return (
    input.marketStatus === 'trading' &&
    input.pipelineStage === 'resolving' &&
    !input.resolutionHolding
  );
}

/**
 * What the resolution card shows for a market, or `null` for no card.
 *
 * Only a market the page presents as `trading` can have one: before trading
 * opens nothing can cross, and once the page settles the ResolvedPanel is the
 * sole post-resolution surface. A landed resolution the page is still holding
 * (`graduating`) wins over the `resolving` stage, which wins over a projection.
 */
export function resolveResolutionCardState(input: ResolutionCardInput): ResolutionCardState | null {
  const {
    marketPubkey,
    marketStatus,
    pipelineStage,
    outcomes,
    outcomeHueByIndex,
    imageUrlByIndex,
    imageNsfwByIndex,
    liveConviction,
    threshold,
    projectedWinner,
    projectedGap,
    resolvingCrossAtMs,
    resolutionHolding,
  } = input;
  if (marketStatus !== 'trading' || pipelineStage === 'resolved') return null;
  if (threshold === null || threshold <= 0) return null;

  const cardOutcome = (outcomeIndex: number): ResolutionCardOutcome | null => {
    const row = (outcomes ?? []).find((o) => Number(o.outcomeIndex) === outcomeIndex);
    const hue = outcomeHueByIndex.get(outcomeIndex);
    if (row === undefined || hue === undefined) return null;
    return {
      marketPubkey,
      outcomeIndex,
      label: row.label,
      hue,
      imageUrl: imageUrlByIndex.get(outcomeIndex) ?? null,
      imageNsfw: imageNsfwByIndex.get(outcomeIndex) === true,
    };
  };

  // The leader reached the threshold; the crank settles it and the page then
  // holds while the settled page's data loads.
  if (resolutionHolding || pipelineStage === 'resolving') {
    const leader = selectConvictionLeader(liveConviction);
    const outcome = leader === null ? null : cardOutcome(leader.outcomeIndex);
    if (outcome === null) return null;
    return {
      kind: resolutionHolding ? 'graduating' : 'resolving',
      outcome,
      threshold,
      crossAtMs: resolvingCrossAtMs,
    };
  }

  // Projected winner (spec 102) — an outcome is on course to cross first.
  if (projectedWinner === null || projectedGap === null) return null;
  const outcome = cardOutcome(projectedWinner.outcomeIndex);
  if (outcome === null) return null;
  return {
    kind: 'projected',
    outcome,
    gap: projectedGap,
    crossAtMs: projectedWinner.crossAtMs,
  };
}
